import { JSX, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { format } from "date-fns";

import Box from "@mui/material/Box";
import Collapse from "@mui/material/Collapse";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";

import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { asTitle } from "utils/Utils";

import {
	latestSnapshot,
	resolveScore,
	snapshotsNewestFirst
} from "evaluation/EffectiveScore";
import { Filtered } from "evaluation/EvaluationUtils";
import { ScoreResolution } from "evaluation/ScoreResolution";
import { Cluster, Criteria, CriteriaType } from "./TrajectoryService";
import ScoreField from "evaluation/ScoreField";

export type EvaluationOverviewProps = {
	filtered: Filtered[];
	criteria: Criteria[];
	scoreNames: string[];
	minScore: number;
	maxScore: number;
	scoreResolution: ScoreResolution;
	// The day whose state to show; absent means the current state.
	asOf?: Date;

	getScoreName: (score: number | null) => string;
	getScoreInfo: (score: number | null) => string;
	getScoreColour: (score: number | null) => string;
};

function EvaluationOverview(props: EvaluationOverviewProps): JSX.Element {
	const { t } = useTranslation();

	const [expanded, setExpanded] = useState(false);

	const [histo, setHisto] = useState<number[]>([]);
	const [levels, setLevels] = useState<number[]>([]);
	const [achieved, setAchieved] = useState(0);
	const [evaluations, setEvaluations] = useState(0);

	useEffect(() => {
		const scores = props.maxScore - props.minScore + 1;
		const histogram: number[] = Array(scores).fill(0);
		const lvls: number[] = [];
		let achieved = 0;
		let evaluations = 0;

		const fn = function (criterion: Criteria, level: number) {
			if (level - 1 >= lvls.length) {
				lvls.push(1);
			} else {
				lvls[level - 1]++;
			}

			if (level === 3) {
				const properties = latestSnapshot(criterion, props.asOf);

				// The shared helper applies the trajectory's score resolution;
				// under TRAILING_ACHIEVED a trailing score-less "behaald"
				// leaves the histogram (it is still counted as achieved below).
				const resolved = resolveScore(
					criterion,
					props.scoreResolution,
					props.asOf
				);
				if (resolved.kind === "score") {
					const bucket = resolved.score - props.minScore;
					if (bucket >= 0 && bucket < histogram.length) {
						histogram[bucket]++;
					}
				}

				const achievedValue = properties?.achieved;
				if (achievedValue === "true") {
					achieved++;
				}

				evaluations += snapshotsNewestFirst(criterion, props.asOf).length;
			}
		};
		traverse(props.filtered, props.criteria, fn);

		setHisto(histogram);
		setLevels(lvls);
		setAchieved(achieved);
		setEvaluations(evaluations);
	}, [
		props.filtered,
		props.criteria,
		props.minScore,
		props.maxScore,
		props.scoreResolution,
		props.asOf
	]);

	function traverse(
		elements: Filtered[],
		criteria: Criteria[],
		fn: (criterion: Criteria, level: number) => void,
		level = 1
	): void {
		elements.forEach(element => {
			const criterion = criteria[element.index];
			fn(criterion, level);
			if (criterion.type === CriteriaType.CLUSTER) {
				const cluster = criterion as Cluster;
				traverse(
					element.subElements,
					cluster.subsections,
					fn,
					level + 1
				);
			}
		});
	}

	const averageScore = useMemo(
		() =>
			histo.reduce((a, b, i) => a + b * (i + props.minScore), 0) /
			histo.reduce((a, b) => a + b, 0),
		[histo, props.minScore]
	);

	function averageRubrics(): string {
		return isNaN(averageScore) ? "" : Math.round(averageScore).toString();
	}

	// The 1-based average level (matching the dashboard scores widget) and
	// that average as a percentage of the highest level, rounded to at most
	// two decimals (e.g. 2.81 of 4 levels -> 70.25%).
	const averageLevel: number = averageScore - props.minScore + 1;
	const averagePercentage: number =
		Math.round(
			(averageLevel / (props.maxScore - props.minScore + 1)) * 100 * 100
		) / 100;

	// The panel names the day it shows, so a historical Overzicht is never
	// mistaken for the current one.
	const heading: string =
		props.asOf === undefined
			? asTitle(t("overview"))
			: `${asTitle(t("overview"))} (${asTitle(t("asOf"))} ${format(
					props.asOf,
					"dd/MM/yyyy"
				)})`;

	return (
		<Box
			sx={{
				marginTop: 2,
				marginBottom: 2,
				paddingRight: 1,
				border: 1,
				borderColor: "divider",
				borderRadius: 1,
				backgroundColor: "background.paper"
			}}>
			<Grid container alignItems={"center"}>
				<Grid item>
					<IconButton onClick={() => setExpanded(!expanded)}>
						{expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
					</IconButton>
				</Grid>
				<Grid item flexGrow={1}>
					<Typography variant="subtitle1">{heading}</Typography>
				</Grid>
			</Grid>
			<Collapse
				in={expanded}
				timeout="auto"
				sx={{
					paddingLeft: 2,
					paddingRight: 2,
					paddingBottom: expanded ? 2 : 0
				}}>
				<Grid container rowSpacing={1} columnSpacing={3}>
					<Grid item lg={2} md={3} xs={6}>
						<Grid container direction="column">
							<Grid item>
								<Typography variant="h6">
									{asTitle(t("competence_other"))}
								</Typography>
							</Grid>
							<Grid item>
								<Table sx={{ maxWidth: 180 }} size="small">
									<TableHead>
										<TableRow>
											<TableCell align="center">
												{asTitle(t("level"))}
											</TableCell>
											<TableCell align="right">
												#
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{levels.map((value, index) => (
											<TableRow
												key={index}
												sx={{ td: { border: 0 } }}>
												<TableCell align="center">
													{index + 1}
												</TableCell>
												<TableCell align="right">
													{value}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Grid>
						</Grid>
					</Grid>

					<Grid item lg={3} md={4} sm={6} xs={6}>
						<Grid container direction="column">
							<Grid item>
								<Typography variant="h6">
									{asTitle(t("score_other"))}
								</Typography>
							</Grid>
							<Grid item>
								<Table sx={{ maxWidth: 260 }} size="small">
									<TableHead>
										<TableRow>
											<TableCell>
												{asTitle(t("score"))}
											</TableCell>
											<TableCell align="right">
												#
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{props.scoreNames.map((name, index) => (
											<TableRow
												key={index}
												sx={{ td: { border: 0 } }}>
												<TableCell>{name}</TableCell>
												<TableCell align="right">
													{histo[index]}
												</TableCell>
											</TableRow>
										))}
									</TableBody>
								</Table>
							</Grid>
						</Grid>
					</Grid>

					<Grid item lg={3} md={4} xs={12}>
						<Grid container rowSpacing={1} columnSpacing={3}>
							<Grid item container md={12} xs={12}>
								<Grid item md={5} xs={3}>
									<Typography variant="subtitle2">
										{asTitle(t("achieved"))}
									</Typography>
								</Grid>
								<Grid item xs={3}>
									<Typography variant="body2">
										{achieved}/{levels[2]}
									</Typography>
								</Grid>
							</Grid>
							<Grid item container md={12} xs={12}>
								<Grid item md={5} xs={3}>
									<Typography variant="subtitle2">
										{asTitle(t("rated"))}
									</Typography>
								</Grid>
								<Grid item xs={3}>
									<Typography variant="body2">
										{histo.reduce((a, b) => a + b, 0)}/
										{levels[2]}
									</Typography>
								</Grid>
							</Grid>
							<Grid item container md={12} xs={12}>
								<Grid item md={5} xs={3}>
									<Typography variant="subtitle2">
										{asTitle(t("evaluation_other"))}
									</Typography>
								</Grid>
								<Grid item xs={3}>
									<Typography variant="body2">
										{evaluations}
									</Typography>
								</Grid>
							</Grid>
							<Grid item container xs={12}>
								<Grid item md={3} xs={3}>
									<Typography variant="subtitle2">
										{asTitle(`${t("average")}`)}
									</Typography>
								</Grid>
								<Grid item md={6} xs={4}>
									<ScoreField
										value={averageRubrics()}
										disabled={true}
										minScore={props.minScore}
										maxScore={props.maxScore}
										getScoreName={props.getScoreName}
										getScoreInfo={props.getScoreInfo}
										getScoreColour={props.getScoreColour}
										onChange={() => {
											throw new Error(
												"Function not implemented."
											);
										}}
									/>
								</Grid>
								{!isNaN(averageScore) && (
									<Grid item md={3} xs={5}>
										({averageLevel.toFixed(2)} -{" "}
										{averagePercentage}%)
									</Grid>
								)}
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Collapse>
		</Box>
	);
}

export default EvaluationOverview;
